import React, { useMemo, useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { syncUser } from "@/services/UserService";
import { useUser } from "@/context/UserContext";
import SelectLocationIcon from "@/assets/icons-v2/select-location-icon.png";
import AppButton from "@/components/common/buttons/app-button";
import useModalState from "@/hooks/useModalState";
import usePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService";
import config from "@/config";
import toast from "react-hot-toast";
import ClipLoader from "react-spinners/ClipLoader";
import { find, isEmpty, /* isNil, */ debounce } from "lodash";
import { TOAST_MESSAGES } from "@/lang/toast-messages.lang";
import GoogleAutoComplete from "@/components/common/google-auto-complete/google-auto-complete";
// import useLocalStorage from "@/hooks/useLocalStorage";
// import { useAuth0 } from "@auth0/auth0-react";
// import useGAEvent from "@/hooks/useGAEvent";

// Initial state area values
const INIT_AREA_VALUES = {
    areaName: "",
    zipCode: "",
    coordinates: {} as Record<string, unknown>,
};

export default function SelectLocationModal(): React.ReactElement {
    // const { isAuthenticated } = useAuth0();
    // const { sendEvent } = useGAEvent();
    const { setModalId, isOpen } = useModalState();
    const { userState, setUserState } = useUser();
    // const [preferredVersion] = useLocalStorage("live-variant-preferred-version");

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDisabled, setIsDisabled] = useState(true);
    const [areaValues, setAreaValues] = useState(INIT_AREA_VALUES);
    const [suggestedAreas, setSuggestedAreas] = useState<any[]>([]);
    const { placePredictions, getPlacePredictions } = usePlacesService({
        apiKey: config.GOOGLE_MAPS_API_KEY,
        options: { componentRestrictions: { country: ["us"] } }, // US only results
    });

    // Debounce the getPlacePredictions function
    const debouncedGetPlacePredictions = useMemo(
        () =>
            debounce((input: string) => {
                if (areaValues?.areaName && areaValues.areaName.length) {
                    getPlacePredictions({ input, fields: ["name", "geometry"] } as any);
                }
            }, 300),
        [placePredictions], // eslint-disable-line react-hooks/exhaustive-deps
    );

    /**
     * Handles the closing of the select location modal.
     */
    const handleSelectLocationModalClose = () => {
        setModalId("");
    };

    // Set suggested areas
    useEffect(() => {
        // Filter place prediction results from Chicago and IL only
        const filteredPlacePredictions = placePredictions.filter(
            (place: any) => find(place?.terms, (term: any) => term.value === "Chicago" || term.value === "IL") ?? false,
        );
        if (filteredPlacePredictions?.length) {
            setSuggestedAreas(filteredPlacePredictions);
        }
    }, [placePredictions]);

    // One-time set initial places
    useEffect(() => {
        if (!isEmpty(userState?.currentLocation)) {
            getPlacePredictions({ input: userState.currentLocation, fields: ["name", "geometry"] } as any);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Handles area request submission
    const handleSubmitRequest = async (e?: any, onEnterPressedValue?: string) => {
        try {
            if (!onEnterPressedValue && e?.preventDefault) {
                e.preventDefault();
            }

            const userLocation = onEnterPressedValue || areaValues.areaName;
            setIsSubmitting(true);

            const updatedUserData: any = { ...userState, currentLocation: userLocation };
            await syncUser(updatedUserData);

            toast.success("Location saved!");

            /* sendEvent("area_select_lets_go", {
				isAuthenticated,
				userLocation,
			}); */

            setIsSubmitting(false);
            setUserState(updatedUserData, false);

            // Remove the previous properties in the local storage if location is updated
            if (userState.currentLocation !== areaValues.areaName) {
                localStorage.removeItem("previousProperties");
            }

            handleSelectLocationModalClose();
        } catch (error) {
            toast.error(TOAST_MESSAGES.LOCATION.INVALID, { id: "LOCATION.INVALID" });
            setIsSubmitting(false);
        }
    };

    // Resets form state on modal close
    useEffect(() => {
        if (!isOpen("LIVE_VARIANT_SELECT_LOCATION_MODAL")) {
            setAreaValues(INIT_AREA_VALUES);
            setSuggestedAreas([]);
        }
    }, [isOpen]);

    /* useEffect(() => {
		if (!isNil(userState.currentLocation) && !isEmpty(userState.currentLocation)) {
			setAreaValues((prev) => ({ ...prev, areaName: userState.currentLocation }));
			setIsDisabled(false);
		}
	}, [userState.currentLocation]); */

    return (
        <Modal
            centered
            backdrop="static"
            data-testid="live-variant-select-location-modal"
            keyboard={false}
            show={isOpen("LIVE_VARIANT_SELECT_LOCATION_MODAL")}
            onHide={() => handleSelectLocationModalClose()}
            className="select-location-modal"
        >
            <Modal.Body className="!tw-pt-0 !tw-pb-0 tw-px-4">
                <div className="select-location-modal-slide-group">
                    <div className="slide-img-wrapper">
                        <img
                            className="select-location-modal-icon house-sold-icon"
                            alt="house-sold-icon"
                            src={SelectLocationIcon}
                        />
                    </div>
                    <h5 className="app-text-title tw-text-center tw-pb-2">Where do you want to play?</h5>
                    <p>Choose a location to start from</p>

                    <form id="select-location-form" className="request-form" onSubmit={handleSubmitRequest}>
                        <GoogleAutoComplete
                            wrapperClassName="tw-mb-4 tw-w-full tw-mx-auto form-control"
                            placeholder="Enter a neighborhood, city or location."
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setIsDisabled(true);
                                debouncedGetPlacePredictions(e.target.value);
                                if (isEmpty(e.target.value)) {
                                    setSuggestedAreas([]);
                                }
                                setAreaValues((prev) => ({ ...prev, areaName: e.target.value }));
                            }}
                            inputValue={areaValues.areaName}
                            suggestedAreas={areaValues.areaName.trim().length ? suggestedAreas : []}
                            onSelect={(sa: any, isEnterPressed?: boolean) => {
                                if (sa) {
                                    setAreaValues((prev) => ({ ...prev, areaName: sa.description }));
                                    if (isEnterPressed) {
                                        handleSubmitRequest({}, sa.description);
                                    }
                                }
                                setIsDisabled(false);
                            }}
                            isLoading={isSubmitting}
                        />
                    </form>
                </div>
            </Modal.Body>
            <Modal.Footer className="tw-flex tw-flex-col !tw-py-6 !tw-pt-0">
                <AppButton
                    isDisabled={isSubmitting || isDisabled}
                    theme="purple"
                    className="!tw-w-3/4 !tw-mb-0 submit-req-btn"
                    type="submit"
                    onClick={handleSubmitRequest}
                >
                    {!isSubmitting ? "Let's go" : <ClipLoader size={15} />}
                </AppButton>
            </Modal.Footer>
        </Modal>
    );
}
